import { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { isNative } from '@ah/native/runtime';
import { disablePush, enablePush, isEnabled } from '../shared/pushClient.ts';

type State =
  | { kind: 'loading' }
  | { kind: 'unsupported' }
  | { kind: 'denied' }
  | { kind: 'off' }
  | { kind: 'on' };

export function PushToggle() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void refresh();
  }, []);

  async function refresh() {
    // On Capacitor iOS, `Notification` is undefined (WKWebView doesn't expose
    // the Web Push API) — but native push via the Capacitor plugin works
    // fine, so we mustn't fall into the 'unsupported' state just because the
    // browser-level check fails. The native enablePush() path doesn't need
    // any of these browser permission states.
    if (isNative()) {
      setState({ kind: (await isEnabled()) ? 'on' : 'off' });
      return;
    }
    if (typeof Notification === 'undefined') {
      setState({ kind: 'unsupported' });
      return;
    }
    if (Notification.permission === 'denied') {
      setState({ kind: 'denied' });
      return;
    }
    setState({ kind: (await isEnabled()) ? 'on' : 'off' });
  }

  async function onToggle() {
    if (busy) return;
    setError(null);

    // Optimistic flip — APNs registration on iOS takes 1–3 seconds and a
    // frozen toggle reads as "my tap didn't work". Flip immediately, revert
    // if the underlying op fails.
    const wasOn = state.kind === 'on';
    setState({ kind: wasOn ? 'off' : 'on' });
    setBusy(true);

    try {
      if (wasOn) {
        await disablePush();
        await refresh();
      } else {
        const r = await enablePush();
        if (!r.ok) {
          setState({ kind: 'off' });
          setError(humanReason(r.reason));
        } else {
          // Reconcile — handles the edge case where iOS forced denial
          // mid-flight (e.g. permission revoked in Settings during the
          // registration round-trip).
          await refresh();
        }
      }
    } catch (err) {
      setState({ kind: wasOn ? 'on' : 'off' });
      setError(humanReason(err instanceof Error ? err.message : String(err)));
    } finally {
      setBusy(false);
    }
  }

  const checked = state.kind === 'on';
  const disabled = busy || state.kind === 'loading' || state.kind === 'unsupported' || state.kind === 'denied';

  return (
    <div className="px-4 py-3 border-b border-border">
      <div className="flex items-center gap-3">
        <div className="size-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Bell size={18} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Push notifications</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {subtitleFor(state)}
          </p>
          {error && <p className="text-xs text-destructive mt-1">{error}</p>}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label="Toggle push notifications"
          onClick={onToggle}
          disabled={disabled}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
            checked ? 'bg-primary' : 'bg-muted'
          }`}
        >
          <span
            className={`inline-block size-5 transform rounded-full bg-background shadow transition-transform ${
              checked ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>
    </div>
  );
}

function subtitleFor(s: State): string {
  switch (s.kind) {
    case 'loading': return 'Checking…';
    case 'unsupported': return 'Not supported in this browser.';
    case 'denied': return 'Blocked in your browser settings — re-enable there to turn this on.';
    case 'on': return 'Enabled on this device.';
    case 'off': return 'Turn on to get nudges for replies, events, and Underling messages.';
  }
}

function humanReason(reason: string): string {
  if (reason === 'unsupported') return 'Not supported in this browser.';
  if (reason === 'missing VAPID key') return 'Push is misconfigured — please report this.';
  if (reason === 'no token returned') return 'Browser refused to provide a push token.';
  return reason;
}
